import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { MaxSizedUnallocatedAmount } from 'components/PostTrade/TradeAllocation/components/UnallocatedAmount';
import { asIsoDate } from 'components/shared/DatePicker/utils';
import { FormattedMessage } from 'components/shared/Intl';
import { PercentWithUnitSkeleton } from 'components/shared/Percent/PercentWithUnit';
import type { Custodian } from 'models/custodian';
import { FC } from 'react';
import Skeleton from 'react-loading-skeleton';
import type { TradeAllocationRowModel } from 'state/tradeAllocationForm';
import { now } from 'utils/core/now';

import { NotionalSkeleton } from '../TradeAllocationNotional';
import { TradeAllocationRow } from '../TradeAllocationRow';

import './TradeAllocationGrid.scss';
export interface TradeAllocationSkeletonGridProps {}
export interface TradeAllocationGridProps {
  allocations: TradeAllocationRowModel[];
  custodians: ReadonlyArray<Custodian>;
  unallocatedNotional: number;
  currency: string;
  isDisabled: boolean;
  hasIndependentAmount: boolean;
  onAllocationChanged: (index: number, allocation: TradeAllocationRowModel) => void;
  onAllocationRemoved: (index: number) => void;
  preComPrice?: number;
  offerPriceUnit?: string;
}

export const TradeAllocationGrid: FC<TradeAllocationGridProps> = ({
  allocations,
  custodians,
  unallocatedNotional,
  currency,
  isDisabled,
  onAllocationChanged,
  onAllocationRemoved,
  hasIndependentAmount,
  preComPrice,
  offerPriceUnit,
}) => {
  const hasAnyIndependentAmount =
    hasIndependentAmount && allocations?.some(allocation => allocation.independentAmount !== undefined);

  return (
    <>
      <table className="table table-sm table-bordered mb-0 trade-allocations-grid">
        <thead>
          <tr>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.dealId" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.tradeDate" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0 text-nowrap">
              <FormattedMessage id="tradeAllocation.header.custodian" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0 text-nowrap">
              <FormattedMessage id="tradeAllocation.header.notional" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.offerPrice" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0 text-nowrap">
              <FormattedMessage id="tradeAllocation.header.distributorUf" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0 text-nowrap">
              <FormattedMessage id="tradeAllocation.header.custodianUf" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.salesMargin" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.valueDate" />
            </th>
            {hasIndependentAmount && (
              <th
                className={classNames(
                  'tradeAllocation-cell text-secondary border-bottom-0 trade-allocation-ia-main-col',
                  {
                    'is-expanded': hasAnyIndependentAmount,
                  },
                )}
              >
                <FormattedMessage
                  id={
                    hasAnyIndependentAmount ? 'tradeAllocation.header.independentAmount' : 'tradeAllocation.header.ia'
                  }
                />
              </th>
            )}
            {hasIndependentAmount && (
              <th
                className={classNames(
                  'tradeAllocation-cell text-secondary border-bottom-0 trade-allocation-ia-value-date-col',
                  {
                    'is-expanded': hasAnyIndependentAmount,
                  },
                )}
              >
                <FormattedMessage id="tradeAllocation.independentAmountValueDate" />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {allocations?.map((allocation, index: number) => (
            <TradeAllocationRow
              allocation={allocation}
              index={index}
              custodians={custodians}
              isDisabled={isDisabled}
              hasIndependentAmount={hasIndependentAmount}
              hasAnyIndependentAmount={hasAnyIndependentAmount}
              onAllocationChanged={a => onAllocationChanged(index, a)}
              onAllocationRemoved={() => onAllocationRemoved(index)}
              key={`trade-allocation-row-${index}`}
            />
          ))}
        </tbody>
      </table>

      <div className="d-flex align-items-center border-0 trade-allocations-footer">
        <AddRowButton
          isDisabled={isDisabled}
          onClick={() => {
            const lastAllocation = allocations[allocations.length - 1];

            const hasOfferPrice = isDefined(preComPrice);

            const newOfferPrice = hasOfferPrice ? preComPrice : lastAllocation.offerPrice;

            const newOfferPriceUnit = isDefined(offerPriceUnit) ? offerPriceUnit : lastAllocation?.offerPriceUnit;

            const newAllocation: TradeAllocationRowModel = {
              ...lastAllocation,
              custodianCode: undefined,
              notional: null,
              dealId: '',
              tradeDate: asIsoDate(now()),
              valueDate: undefined,
              isManuallyAdded: true,
              isExpanded: false,
              independentAmount: undefined,
              offerPrice: newOfferPrice,
              offerPriceUnit: newOfferPriceUnit,
              hasOfferPrice: hasOfferPrice,
            };
            onAllocationChanged(allocations.length, newAllocation);
          }}
        />
        <span className="flex-fill" />
        <MaxSizedUnallocatedAmount
          amount={unallocatedNotional}
          currency={currency}
          hasWarning={unallocatedNotional !== 0}
          isDisabled={isDisabled}
        />
      </div>
    </>
  );
};

export const TradeAllocationGridSkeleton: FC<TradeAllocationSkeletonGridProps> = () => {
  return (
    <>
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.dealId" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.tradeDate" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.custodian" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.notional" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.offerPrice" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.distributorUf" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.custodianUf" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.salesMargin" />
            </th>
            <th className="tradeAllocation-cell text-secondary border-bottom-0">
              <FormattedMessage id="tradeAllocation.header.valueDate" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="form-control readonly">
                <Skeleton width="6em" />
              </div>
            </td>
            <td>
              <div>
                <span className="form-control">
                  <span className="pe-2">
                    <Skeleton width="1em" />
                  </span>
                  <Skeleton width="4em" />
                </span>
              </div>
            </td>
            <td>
              <div className="form-control">
                <Skeleton width="6em" />
              </div>
            </td>
            <td>
              <NotionalSkeleton />
            </td>
            <td>
              <PercentWithUnitSkeleton />
            </td>
            <td>
              <PercentWithUnitSkeleton />
            </td>
            <td>
              <PercentWithUnitSkeleton />
            </td>

            <td>
              <PercentWithUnitSkeleton />
            </td>
            <td>
              <div className="form-control">
                <Skeleton width="12em" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export interface AddRowButtonProps {
  onClick: () => void;
  isDisabled: boolean;
}

export const AddRowButton: FC<AddRowButtonProps> = ({ onClick, isDisabled }) => (
  <button
    className="btn btn-sm btn-flat-primary btn-icon-start p-2 mt-3"
    onClick={onClick}
    data-e2e="add-row"
    disabled={isDisabled}
  >
    <i className="icon">add</i>
    <FormattedMessage id="tradeAllocation.addRow" />
  </button>
);
